import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';
import { toast } from 'react-toastify';

const Gallery = () => {
  const navigate = useNavigate();
  const mediaUrl = process.env.REACT_APP_MEDIA_URL;
  const [galleries, setGalleries] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Sort Index Modal State
  const [showSortIndexModal, setShowSortIndexModal] = useState(false);
  const [sortModalData, setSortModalData] = useState({
    id: '',
    locationName: '',
    image: '',
    sortIndex: 0
  });
  const [updatingSort, setUpdatingSort] = useState(false);

  // Drag and Drop Reordering State
  const [draggedIdx, setDraggedIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);

  // Single Add / Edit Form State
  const [formData, setFormData] = useState({
    id: '',
    locationName: '',
    sortIndex: 0
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState('');
  const [deleteId, setDeleteId] = useState('');

  // Bulk Upload State
  const [bulkFiles, setBulkFiles] = useState([]);
  const [bulkDefaultLocation, setBulkDefaultLocation] = useState('');

  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchGalleries();
  }, []);

  const fetchGalleries = async () => {
    try {
      const response = await apiService.get('galleryList');
      if (response.data) {
        setGalleries(response.data);
      }
    } catch (error) {
      console.error('Error fetching gallery list:', error);
    }
  };

  const handleDragStart = (e, index) => {
    setDraggedIdx(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragOverIdx !== index) {
      setDragOverIdx(index);
    }
  };

  const handleDrop = async (e, targetIndex) => {
    e.preventDefault();
    if (draggedIdx === null || draggedIdx === targetIndex) {
      setDraggedIdx(null);
      setDragOverIdx(null);
      return;
    }

    const items = [...galleries];
    const [movedItem] = items.splice(draggedIdx, 1);
    items.splice(targetIndex, 0, movedItem);

    const reorderedItems = items.map((item, idx) => ({
      ...item,
      sortIndex: idx + 1
    }));

    setGalleries(reorderedItems);
    setDraggedIdx(null);
    setDragOverIdx(null);

    try {
      const payload = reorderedItems.map(item => ({
        id: item._id,
        sortIndex: item.sortIndex
      }));
      const response = await apiService.post('reorderGallery', { items: payload });
      if (response.status) {
        toast.success(response.message || 'Gallery order updated successfully');
        fetchGalleries();
      } else {
        toast.error(response.message || 'Failed to reorder gallery');
        fetchGalleries();
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to save reordered list');
      fetchGalleries();
    }
  };

  const handleDragEnd = () => {
    setDraggedIdx(null);
    setDragOverIdx(null);
  };

  const openAddModal = () => {
    setFormData({ id: '', locationName: '', sortIndex: galleries.length + 1 });
    setImageFile(null);
    setImagePreview('');
    setSaving(false);
    setShowModal(true);
  };

  const openBulkModal = () => {
    setBulkFiles([]);
    setBulkDefaultLocation('');
    setSaving(false);
    setShowBulkModal(true);
  };

  const openSortModal = (item) => {
    setSortModalData({
      id: item._id,
      locationName: item.locationName || '',
      image: item.image || '',
      sortIndex: item.sortIndex !== undefined && item.sortIndex !== null ? item.sortIndex : 0
    });
    setUpdatingSort(false);
    setShowSortIndexModal(true);
  };

  const editGalleryItem = (item) => {
    setFormData({
      id: item._id,
      locationName: item.locationName || '',
      sortIndex: item.sortIndex !== undefined && item.sortIndex !== null ? item.sortIndex : 0
    });
    setImageFile(null);
    setSaving(false);
    if (item.image) {
      setImagePreview(`${mediaUrl}${item.image}`);
    } else {
      setImagePreview('');
    }
    setShowModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Image size must be less than 5MB');
        e.target.value = '';
        return;
      }
      setImageFile(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id && !imageFile) {
      toast.error('Please select file');
      return;
    }
    if (saving) return;
    setSaving(true);
    try {
      const submitData = new FormData();
      if (formData.id) submitData.append('id', formData.id);
      submitData.append('locationName', formData.locationName);
      submitData.append('sortIndex', formData.sortIndex !== undefined && formData.sortIndex !== null ? formData.sortIndex : 0);
      if (imageFile) submitData.append('image', imageFile);

      const response = await apiService.postFormData('saveGallery', submitData);

      if (response.status) {
        toast.success(formData.id ? 'Gallery item updated successfully' : 'Gallery item added successfully');
        setShowModal(false);
        fetchGalleries();
      } else {
        toast.error(response.message || 'Error saving gallery item');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleSortModalSubmit = async (e) => {
    e.preventDefault();
    if (!sortModalData.id) return;
    if (updatingSort) return;
    setUpdatingSort(true);
    try {
      const val = parseInt(sortModalData.sortIndex, 10);
      const response = await apiService.post('updateGallerySortIndex', {
        id: sortModalData.id,
        sortIndex: isNaN(val) ? 0 : val
      });
      if (response.status) {
        toast.success(response.message || 'Sort index updated successfully');
        setShowSortIndexModal(false);
        fetchGalleries();
      } else {
        toast.error(response.message || 'Error updating sort index');
      }
    } catch (error) {
      console.error(error);
      toast.error('Failed to update sort index');
    } finally {
      setUpdatingSort(false);
    }
  };

  // Bulk File Selection & Batch Operations
  const handleBulkFilesSelect = (e) => {
    const selectedFiles = Array.from(e.target.files || []);
    if (selectedFiles.length === 0) return;

    const validFiles = selectedFiles.filter(f => f.size <= 5 * 1024 * 1024);
    if (validFiles.length < selectedFiles.length) {
      toast.error('Some files exceeded the 5MB size limit and were skipped.');
    }

    const startSortIdx = galleries.length + 1;
    const newEntries = validFiles.map((file, index) => ({
      id: `${Date.now()}-${index}-${Math.random()}`,
      file,
      preview: URL.createObjectURL(file),
      locationName: bulkDefaultLocation,
      sortIndex: startSortIdx + index
    }));

    setBulkFiles(prev => [...prev, ...newEntries]);
    e.target.value = '';
  };

  const applyBulkLocationToAll = () => {
    setBulkFiles(prev => prev.map(item => ({ ...item, locationName: bulkDefaultLocation })));
    toast.info('Applied location to all selected photos');
  };

  const updateBulkItem = (id, field, value) => {
    setBulkFiles(prev => prev.map(item => item.id === id ? { ...item, [field]: value } : item));
  };

  const removeBulkItem = (id) => {
    setBulkFiles(prev => prev.filter(item => item.id !== id));
  };

  const handleBulkSubmit = async (e) => {
    e.preventDefault();
    if (bulkFiles.length === 0) {
      toast.error('Please select at least one photo for bulk upload');
      return;
    }
    if (saving) return;
    setSaving(true);

    try {
      const submitData = new FormData();
      const itemsMetadata = [];

      bulkFiles.forEach((item, idx) => {
        submitData.append('images', item.file);
        itemsMetadata.push({
          locationName: item.locationName || '',
          sortIndex: item.sortIndex !== undefined && item.sortIndex !== null ? item.sortIndex : (galleries.length + idx + 1)
        });
      });

      submitData.append('itemsData', JSON.stringify(itemsMetadata));

      const response = await apiService.postFormData('saveBulkGallery', submitData);

      if (response.status) {
        toast.success(response.message || `${bulkFiles.length} photos uploaded successfully`);
        setShowBulkModal(false);
        setBulkFiles([]);
        fetchGalleries();
      } else {
        toast.error(response.message || 'Error uploading bulk photos');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (id) => {
    try {
      await apiService.get(`galleryStatus/${id}`);
      fetchGalleries();
    } catch (error) {
      toast.error('Something went wrong!');
      console.error(error);
    }
  };

  const confirmDelete = (id) => {
    setDeleteId(id);
    setDeleting(false);
    setShowDeleteModal(true);
  };

  const deleteGalleryItem = async () => {
    if (deleting) return;
    setDeleting(true);
    try {
      const response = await apiService.get(`deleteGallery/${deleteId}`);
      if (response.status) {
        toast.success('Gallery item deleted successfully');
        setShowDeleteModal(false);
        fetchGalleries();
      } else {
        toast.error(response.message || 'Error deleting gallery item');
      }
    } catch (error) {
      toast.error('Something went wrong, try again!!');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-slate-50 min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* Page Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-2xl shadow-xs border border-slate-200/80">
          <div>
            <h1 className="text-xl font-extrabold text-slate-900 tracking-tight">Wall Gallery</h1>
            <p className="text-xs text-slate-500 font-normal mt-0.5">
              Manage dynamic photos and moments displayed on the home page wall gallery marquee
            </p>
          </div>
          <div className="flex items-center gap-3 self-start sm:self-auto">
            <button
              onClick={openBulkModal}
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              Bulk Add Photos
            </button>
            <button
              onClick={openAddModal}
              className="inline-flex items-center gap-2 bg-[#089dca] hover:bg-[#078bb3] text-white px-4 py-2.5 rounded-xl font-bold text-xs shadow-md transition-all cursor-pointer"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
              </svg>
              Single Photo
            </button>
          </div>
        </div>

        {/* Table Container */}
        <div className="bg-white rounded-2xl shadow-xs border border-slate-200/80 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50/80 border-b border-slate-200 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                  <th className="w-10 px-3 py-3.5 text-center"></th>
                  <th className="px-6 py-3.5">Sort Value</th>
                  <th className="px-6 py-3.5">Image</th>
                  <th className="px-6 py-3.5">Location</th>
                  <th className="px-6 py-3.5">Date &amp; Time</th>
                  <th className="px-6 py-3.5">Status</th>
                  <th className="px-6 py-3.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium text-slate-700">
                {galleries.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-slate-400">
                      No gallery items found. Click "Bulk Add Photos" or "Single Photo" to get started.
                    </td>
                  </tr>
                ) : (
                  galleries.map((item, index) => (
                    <tr
                      key={item._id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                      className={`transition-colors cursor-grab active:cursor-grabbing ${
                        draggedIdx === index ? 'opacity-30 bg-sky-100/80 border-2 border-dashed border-[#089dca]' : ''
                      } ${
                        dragOverIdx === index && draggedIdx !== index ? 'border-t-2 border-[#089dca] bg-sky-50/90' : 'hover:bg-slate-50/60'
                      }`}
                    >
                      <td className="px-3 py-3.5 text-center text-slate-400 hover:text-slate-600">
                        <svg className="w-4 h-4 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M7 4a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm6-12a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2zm0 6a1 1 0 100-2 1 1 0 000 2z" />
                        </svg>
                      </td>
                      <td className="px-6 py-3.5">
                        <button
                          type="button"
                          onClick={() => openSortModal(item)}
                          className="px-3 py-1 rounded-lg bg-sky-50 hover:bg-sky-100 text-[#089dca] border border-sky-200/90 font-extrabold text-xs shadow-2xs hover:shadow-xs transition-all cursor-pointer"
                          title="Click to change sort index"
                        >
                          {item.sortIndex !== undefined && item.sortIndex !== null ? item.sortIndex : 0}
                        </button>
                      </td>
                      <td className="px-6 py-3.5">
                        <div className="w-16 h-12 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 relative group">
                          {item.image ? (
                            <img
                              src={`${mediaUrl}${item.image}`}
                              alt={item.locationName || 'Gallery Item'}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = '/placeholder.png';
                              }}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400 text-[10px]">
                              No Image
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-3.5 font-semibold text-slate-800">
                        {item.locationName || <span className="text-slate-400 font-normal">--</span>}
                      </td>
                      <td className="px-6 py-3.5">
                        {item.createdAt ? (
                          <div>
                            <div className="text-xs font-semibold text-slate-800">
                              {new Date(item.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </div>
                            <div className="text-[11px] text-slate-400 font-normal">
                              {new Date(item.createdAt).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                            </div>
                          </div>
                        ) : (
                          <span className="text-slate-400 font-normal">--</span>
                        )}
                      </td>
                      <td className="px-6 py-3.5">
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={item.status === 1}
                            onChange={() => toggleStatus(item._id)}
                            className="sr-only peer"
                          />
                          <div className="w-9 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-[#089dca]"></div>
                        </label>
                      </td>
                      <td className="px-6 py-3.5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => editGalleryItem(item)}
                            className="p-1.5 rounded-lg text-slate-600 hover:text-[#089dca] hover:bg-sky-50 transition-all cursor-pointer"
                            title="Edit Item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                          <button
                            onClick={() => confirmDelete(item._id)}
                            className="p-1.5 rounded-lg text-slate-600 hover:text-red-600 hover:bg-red-50 transition-all cursor-pointer"
                            title="Delete Item"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>

      {/* Change Sort Index Modal */}
      {showSortIndexModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-md p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 mb-4">
              <h3 className="text-base font-bold text-slate-900">Change Sort Index</h3>
              <button
                onClick={() => setShowSortIndexModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSortModalSubmit} className="space-y-4">
              {sortModalData.image && (
                <div className="flex items-center gap-3 p-2.5 bg-slate-50 rounded-xl border border-slate-200/80">
                  <div className="w-14 h-12 rounded-lg overflow-hidden border border-slate-200 bg-white shrink-0">
                    <img src={`${mediaUrl}${sortModalData.image}`} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                  <div className="truncate">
                    <p className="text-xs font-bold text-slate-800 truncate">
                      {sortModalData.locationName || 'Wall Gallery Photo'}
                    </p>
                    <p className="text-[11px] text-slate-400">Current Position Index</p>
                  </div>
                </div>
              )}

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  New Sort Index Number
                </label>
                <input
                  type="number"
                  autoFocus
                  value={sortModalData.sortIndex}
                  onChange={(e) => setSortModalData({ ...sortModalData, sortIndex: e.target.value })}
                  placeholder="e.g. 1, 2, 3..."
                  className="w-full text-sm font-bold px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#089dca] focus:ring-1 focus:ring-[#089dca] transition-all"
                />
                <p className="text-[11px] text-slate-400 mt-1">
                  Lower values (e.g. 1, 2, 3) display first in the gallery marquee.
                </p>
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  disabled={updatingSort}
                  onClick={() => setShowSortIndexModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50 transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updatingSort}
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#089dca] hover:bg-[#078bb3] disabled:opacity-70 shadow-md transition-all cursor-pointer flex items-center gap-2"
                >
                  {updatingSort ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Updating...</span>
                    </>
                  ) : (
                    <span>Update Index</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Single Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50 shrink-0">
              <h3 className="text-base font-bold text-slate-900">
                {formData.id ? 'Edit Gallery Photo' : 'Add New Gallery Photo'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-slate-400 hover:text-slate-600 p-1 rounded-lg transition-colors cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Location Name <span className="text-slate-400 font-normal">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={formData.locationName}
                  onChange={(e) => setFormData({ ...formData, locationName: e.target.value })}
                  placeholder="e.g. Manali, Himachal Pradesh"
                  className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#089dca] focus:ring-1 focus:ring-[#089dca] transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Sort Index Number <span className="text-slate-400 font-normal">(Lower numbers display first)</span>
                </label>
                <input
                  type="number"
                  value={formData.sortIndex}
                  onChange={(e) => setFormData({ ...formData, sortIndex: e.target.value })}
                  placeholder="e.g. 1, 2, 3..."
                  className="w-full text-xs px-3.5 py-2.5 rounded-xl border border-slate-200 focus:outline-none focus:border-[#089dca] focus:ring-1 focus:ring-[#089dca] transition-all"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">
                  Photo <span className="text-red-500">*</span>
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="w-full text-xs text-slate-500 file:mr-4 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-bold file:bg-sky-50 file:text-[#089dca] hover:file:bg-sky-100 transition-all cursor-pointer"
                />
                {imagePreview && (
                  <div className="mt-3 relative w-full h-44 rounded-xl overflow-hidden border border-slate-200">
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#089dca] hover:bg-[#078bb3] disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none shadow-md transition-all cursor-pointer flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Photo</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Bulk Add Photos Modal */}
      {showBulkModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in duration-200">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100 bg-slate-50/80 shrink-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-bold text-slate-900">Bulk Add Gallery Photos</h3>
                <span className="text-[11px] text-slate-400 font-normal hidden sm:inline">• Select photos & customize location</span>
              </div>
              <button
                onClick={() => setShowBulkModal(false)}
                disabled={saving}
                className="text-slate-400 hover:text-slate-600 p-0.5 rounded-lg transition-colors cursor-pointer disabled:pointer-events-none"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Bulk Controls & File Input */}
            <div className="px-4 py-2 border-b border-slate-100 bg-slate-50/30 shrink-0 space-y-2">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-1">
                  <span className="text-[11px] font-bold text-slate-700 whitespace-nowrap">Choose Photos:</span>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleBulkFilesSelect}
                    className="text-xs text-slate-500 file:mr-2 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-[11px] file:font-bold file:bg-emerald-50 file:text-emerald-700 hover:file:bg-emerald-100 transition-all cursor-pointer"
                  />
                </div>
                <div className="text-[11px] text-slate-600 font-semibold bg-white px-2.5 py-0.5 rounded-md border border-slate-200 shadow-2xs shrink-0">
                  Selected: <span className="text-emerald-600 font-bold ml-0.5">{bulkFiles.length}</span>
                </div>
              </div>

              {/* Fast Batch Fill Controls */}
              {bulkFiles.length > 0 && (
                <div className="flex items-center gap-2 p-1.5 bg-white rounded-lg border border-slate-200/90">
                  <input
                    type="text"
                    value={bulkDefaultLocation}
                    onChange={(e) => setBulkDefaultLocation(e.target.value)}
                    placeholder="Default Location for All (e.g. Manali, Himachal Pradesh)"
                    className="flex-1 text-xs px-2.5 py-1 rounded-md border border-slate-200 focus:outline-none focus:border-emerald-600"
                  />
                  <button
                    type="button"
                    onClick={applyBulkLocationToAll}
                    className="text-xs font-bold text-white bg-slate-800 hover:bg-slate-900 px-3 py-1 rounded-md transition-colors cursor-pointer shrink-0"
                  >
                    Apply Location to All
                  </button>
                </div>
              )}
            </div>

            {/* Bulk Items Form List */}
            <form onSubmit={handleBulkSubmit} className="flex-1 overflow-y-auto p-4 space-y-3">
              {bulkFiles.length === 0 ? (
                <div className="py-12 text-center text-slate-400 space-y-2 border-2 border-dashed border-slate-200 rounded-2xl">
                  <svg className="w-10 h-10 mx-auto text-slate-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-xs font-semibold text-slate-500">No photos selected yet.</p>
                  <p className="text-[11px] text-slate-400">Click "Choose Photos" above to add photos to bulk queue.</p>
                </div>
              ) : (
                bulkFiles.map((item, idx) => (
                  <div key={item.id} className="p-3 bg-white rounded-xl border border-slate-200 shadow-2xs flex items-center gap-4 group">
                    <div className="w-20 h-16 rounded-lg overflow-hidden border border-slate-200 bg-slate-100 shrink-0 relative">
                      <img src={item.preview} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover" />
                      <span className="absolute top-1 left-1 bg-slate-900/80 text-white text-[9px] font-bold px-1.5 py-0.5 rounded-md">
                        #{idx + 1}
                      </span>
                    </div>

                    <div className="flex-1">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Location Name</label>
                      <input
                        type="text"
                        value={item.locationName}
                        onChange={(e) => updateBulkItem(item.id, 'locationName', e.target.value)}
                        placeholder="e.g. Manali, Himachal Pradesh"
                        className="w-full text-xs px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:border-emerald-600"
                      />
                    </div>

                    <div className="w-24 shrink-0">
                      <label className="block text-[11px] font-bold text-slate-600 mb-1">Sort Index</label>
                      <input
                        type="number"
                        value={item.sortIndex}
                        onChange={(e) => updateBulkItem(item.id, 'sortIndex', e.target.value)}
                        className="w-full text-xs px-3 py-1.5 rounded-lg border border-slate-200 focus:outline-none focus:border-emerald-600 text-center font-bold"
                      />
                    </div>

                    <button
                      type="button"
                      onClick={() => removeBulkItem(item.id)}
                      className="p-2 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer shrink-0"
                      title="Remove photo"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))
              )}

              <div className="pt-4 flex items-center justify-end gap-3 border-t border-slate-100">
                <button
                  type="button"
                  disabled={saving}
                  onClick={() => setShowBulkModal(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none transition-colors cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving || bulkFiles.length === 0}
                  className="px-6 py-2.5 rounded-xl text-xs font-bold text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed disabled:pointer-events-none shadow-md transition-all cursor-pointer flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                      <span>Uploading {bulkFiles.length} Photos...</span>
                    </>
                  ) : (
                    <span>Upload {bulkFiles.length} Photos</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 w-full max-w-md p-6 text-center space-y-4">
            <div className="w-12 h-12 rounded-full bg-red-50 text-red-500 flex items-center justify-center mx-auto">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900">Delete Gallery Item</h3>
              <p className="text-xs text-slate-500 mt-1">
                Are you sure you want to delete this photo from the wall gallery?
              </p>
            </div>
            <div className="flex items-center justify-center gap-3 pt-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={deleteGalleryItem}
                disabled={deleting}
                className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-70 disabled:cursor-not-allowed disabled:pointer-events-none shadow-md transition-all cursor-pointer flex items-center gap-2"
              >
                {deleting ? (
                  <>
                    <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                    <span>Deleting...</span>
                  </>
                ) : (
                  <span>Yes, Delete</span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;
